import { useAuth } from "../../lib/auth";
import { profileKey } from "../../lib/profileSession";
import { useEffect, useRef, useState } from "react";
import { FileText, X } from "lucide-react";
import { api, type EmploymentWorkspace } from "../../lib/api";
import { captureProfileGuard } from "../../lib/profileSession";
import { AgreementDocument } from "../../components/AgreementDocument";
import { Modal } from "../../components/beui/Modal";
import { Button } from "../../components/beui/Button";

export function EmploymentDocumentAction({ id }: { id: string }) {
  const { user } = useAuth();
  return <EmploymentDocumentButton key={`${profileKey(user)}:${id}`} id={id} />;
}

function EmploymentDocumentButton({ id }: { id: string }) {
  const [open, setOpen] = useState(false), [workspace, setWorkspace] = useState<EmploymentWorkspace | null>(null);
  const [selected, setSelected] = useState(""), [error, setError] = useState("");
  const request = useRef(0);
  useEffect(() => () => { request.current++; }, [id]);
  function close() { request.current++; setOpen(false); setWorkspace(null); setError(""); }
  async function load() {
    const generation = ++request.current, guard = captureProfileGuard();
    setOpen(true); setWorkspace(null); setError("");
    try {
      const next = await api.employmentAgreement(id); guard();
      if (generation !== request.current) return;
      setWorkspace(next); setSelected(next.currentVersion?.id ?? next.history[0]?.id ?? "");
    } catch (failure) {
      if (generation !== request.current) return;
      if (failure instanceof DOMException && failure.name === "AbortError") { close(); return; }
      setError(failure instanceof Error ? failure.message : "Could not load the agreement. Retry.");
    }
  }
  const version = workspace?.history.find(row => row.id === selected);
  return <><button type="button" onClick={() => void load()}><FileText className="h-4 w-4" aria-hidden="true" />View agreement</button>
    {open && <Modal label="Employment agreement" onClose={close} className="employment-document-reader">
      <div className="flex items-center justify-between gap-3 border-b border-line p-5"><h2 className="font-bold">{version ? `Employment version ${version.version}` : "Employment agreement"}</h2><Button variant="ghost" size="icon" aria-label="Close agreement" onClick={close}><X className="h-5 w-5" /></Button></div>
      <div className="space-y-4 p-5">
        {error ? <><p role="alert" className="text-sm text-brand-red">{error}</p><Button variant="outline" onClick={() => void load()}>Retry</Button></> : !workspace ? <p role="status">Loading agreement…</p> : !version ? <p>No signing document is ready yet. The Agreement tab shows its preparation status.</p> : <>
          <div className="flex flex-wrap items-center gap-3"><Button variant="outline" onClick={() => { const generation = request.current; void api.downloadEmployment(id, version.id).catch(failure => { if (generation === request.current) setError(failure instanceof Error ? failure.message : "Could not download the agreement."); }); }}>Download PDF</Button>
            {workspace.history.length > 1 && <label>Version <select className="field w-auto" value={selected} onChange={event => setSelected(event.target.value)}>{workspace.history.map(row => <option key={row.id} value={row.id}>{row.version}{row.id === workspace.currentVersion?.id ? " · Current" : " · Earlier"}</option>)}</select></label>}</div>
          <AgreementDocument document={version.document} />
        </>}
      </div>
    </Modal>}
  </>;
}
